/**
 * Cliente HTTP compartilhado dos módulos admin do console.
 *
 * Antes (2026-09-29) cada `src/modules/<svc>/api.ts` tinha a sua cópia de
 * `baseUrl()`, `request<T>()` e `buildQuery()` — iguais a menos do nome do
 * serviço e da classe de erro. Este módulo é a cópia única; cada serviço
 * continua dono da sua classe de erro (os chamadores fazem `instanceof`).
 */
import { getRuntimeConfig, type RuntimeConfig } from '@/lib/runtime-config'

export interface AdminRequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

/** Constrói a classe de erro do serviço (ex.: `new ClioAdminError(...)`). */
type AdminErrorFactory = (message: string, status: number, body: unknown) => Error

type ApiKey = keyof RuntimeConfig['api']

/**
 * Lê `runtime-config.api.<key>` e tira a barra final. Lança `Error` (ou o que
 * `onMissing` devolver, para serviços cuja classe de erro cobre a config
 * ausente) se a chave não estiver configurada.
 */
export async function resolveApiBase(
  key: ApiKey,
  onMissing?: (message: string) => Error,
): Promise<string> {
  const config = await getRuntimeConfig()
  const url = config.api[key]
  if (!url) {
    const message = `runtime-config.api.${key} is not configured`
    throw onMissing ? onMissing(message) : new Error(message)
  }
  return url.replace(/\/$/, '')
}

/** Como `resolveApiBase`, mas devolve `null` se a chave não estiver configurada. */
export async function resolveOptionalApiBase(key: ApiKey): Promise<string | null> {
  const config = await getRuntimeConfig()
  const url = config.api[key]
  return url ? url.replace(/\/$/, '') : null
}

/** `?a=1&b=2`, pulando `undefined`, `null` e `''`; `''` se nada sobrar. */
export function buildQuery(params: object): string {
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    usp.set(key, String(value))
  }
  const query = usp.toString()
  return query ? `?${query}` : ''
}

/** Corpo de uma resposta de erro: JSON se der, senão texto, senão `null`. */
export async function readErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return await res.text().catch(() => null)
  }
}

interface TenantAdminRequestInit {
  /** Raiz do serviço, sem barra final (ver `resolveApiBase`). */
  root: string
  path: string
  /**
   * Valor de `X-Tenant-Id`. `null`/`undefined`: o cabeçalho não é enviado
   * (serviços sem tenant, ou que passam o tenant em `options.headers`).
   */
  tenantId?: string | null
  /** Prefixo da mensagem de erro, ex.: `'Clio admin'`. */
  label: string
  makeError: AdminErrorFactory
  options?: AdminRequestOptions
  /** `true`: 204 resolve `undefined` em vez de tentar ler JSON. */
  noContentAsUndefined?: boolean
  /**
   * `true`: lê o corpo como JSON ANTES de checar `res.ok` (`null` se não for
   * JSON, sem cair para texto) e devolve esse corpo; o erro leva o mesmo corpo.
   */
  lenientBody?: boolean
}

/**
 * Request JSON autenticado (Bearer da sessão Cerberus + `X-Tenant-Id`).
 * Resposta não-2xx vira `makeError('<label> <METHOD> <path> -> <status>',
 * status, corpo)`.
 */
export async function tenantAdminRequest<T>({
  root,
  path,
  tenantId,
  label,
  makeError,
  options = {},
  noContentAsUndefined = false,
  lenientBody = false,
}: TenantAdminRequestInit): Promise<T> {
  const { getAccessToken } = await import('@/lib/cerberus/tokens')
  const accessToken = getAccessToken()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(tenantId != null ? { 'X-Tenant-Id': tenantId } : {}),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
  }
  const res = await fetch(`${root}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  })
  if (lenientBody) {
    let body: unknown = null
    try {
      body = await res.json()
    } catch {
      body = null
    }
    if (!res.ok) {
      throw makeError(`${label} ${options.method ?? 'GET'} ${path} -> ${res.status}`, res.status, body)
    }
    return body as T
  }
  if (!res.ok) {
    const body = await readErrorBody(res)
    throw makeError(`${label} ${options.method ?? 'GET'} ${path} -> ${res.status}`, res.status, body)
  }
  if (noContentAsUndefined && res.status === 204) return undefined as T
  return (await res.json()) as T
}
